/**
 * AuctionBreak.js
 *
 * Pause screen shown to everyone when the host's "break after X sales" kicks in.
 * Large enough for a projector. Shows:
 * - Items sold so far
 * - Each team's budget left (if the host allows it)
 * - Each team's items (if the host allows it)
 * Players always see their own team's budget and items.
 * The host resumes the auction from here.
 */

import React, { useState } from "react";
import { Play } from "lucide-react";
import toast from "react-hot-toast";
import { getRoomLibrary, getTeamItems } from "../library/libraries";
import { getTeamColor, sortTeams } from "../assets/teamColors";
import { getAuctionSettings } from "../assets/Rules";
import { resumeAuction } from "../helpers/auctionLobby";

const AuctionBreak = ({ room, teams, isAdmin, userTeam }) => {
    const [isResuming, setIsResuming] = useState(false);
    const settings = getAuctionSettings(room);
    const library = getRoomLibrary(room);

    // Only teams that are actually playing
    const playingTeams = sortTeams(teams).filter((team) => team.members?.length > 0);

    const handleResume = async () => {
        setIsResuming(true);
        const resumed = await resumeAuction(room.id);
        if (!resumed) {
            toast.error("Couldn't resume the auction");
            setIsResuming(false);
        }
    };

    return (
        <div className="w-full max-w-[110rem] mx-auto px-6 md:px-12 pt-24 pb-12 text-white">

            {/* --- Header --- */}
            <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-10">
                <div>
                    <p className="text-2xl md:text-3xl text-yellow font-semibold">Break time</p>
                    <div className="mt-2 font-extrabold leading-none text-[clamp(3.5rem,9vw,8rem)]">
                        {room.moviesSold || 0}
                        <span className="text-zinc-500"> / {library.items.length}</span>
                    </div>
                    <p className="mt-2 text-2xl md:text-3xl text-zinc-400">sold · {library.name}</p>
                </div>

                {isAdmin ? (
                    <button
                        onClick={handleResume}
                        disabled={isResuming}
                        className="inline-flex items-center justify-center gap-4 rounded-3xl bg-gradient-to-br from-yellow via-peach to-coral px-14 py-8 text-4xl md:text-5xl font-extrabold text-black shadow-2xl transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40"
                    >
                        <Play className="w-10 h-10 md:w-12 md:h-12" fill="currentColor" />
                        {isResuming ? "Resuming..." : "Resume Auction"}
                    </button>
                ) : (
                    <div className="inline-flex items-center gap-3 rounded-2xl border border-zinc-800 bg-zinc-950 px-6 py-4 text-xl text-zinc-300">
                        <span className="w-2 h-2 rounded-full bg-yellow animate-pulse" />
                        The auction resumes when the host is ready
                    </div>
                )}
            </div>

            {/* --- Teams --- */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-6">
                {playingTeams.map((team) => {
                    const isUserTeam = team.id === userTeam?.id;
                    const showBudget = settings.showBudgets || isUserTeam;
                    const showItems = settings.showInventories || isUserTeam;
                    const items = getTeamItems(team);

                    return (
                        <div
                            key={team.id}
                            className={`rounded-3xl border bg-zinc-950 overflow-hidden ${isUserTeam ? "border-yellow" : "border-zinc-800"}`}
                        >
                            <div className={`h-3 ${getTeamColor(team).bg}`} />
                            <div className="p-6">
                                <div className="text-3xl font-bold">
                                    {team.name}
                                    {isUserTeam && <span className="ml-3 text-lg font-medium text-yellow">You</span>}
                                </div>

                                <div className="mt-4 flex items-baseline justify-between gap-4">
                                    <span className="text-2xl text-zinc-400">{items.length} / {settings.maxItemsPerTeam} items</span>
                                    {showBudget && (
                                        <span className="text-3xl font-extrabold text-yellow">₹{team.budgetLeft ?? 0}</span>
                                    )}
                                </div>

                                {showItems && (
                                    items.length > 0 ? (
                                        <ul className="mt-5 space-y-3 border-t border-zinc-800 pt-5">
                                            {items.map((item, index) => (
                                                <li key={index}>
                                                    <div className="text-xl font-semibold leading-tight">{item.name}</div>
                                                    <div className="text-lg text-zinc-400">
                                                        {library.scoreAttribute} {item.score}
                                                        {settings.showSales && item.soldPrice !== undefined && ` · ₹${item.soldPrice}`}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="mt-5 border-t border-zinc-800 pt-5 text-xl text-zinc-600">Nothing yet</p>
                                    )
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AuctionBreak;
